import React, { useCallback, useContext, useEffect, useState } from "react";

const preferDarkQuery = "(prefers-color-scheme: dark)";
const DarkModeContext = React.createContext(false);
const DarkModeActionContext = React.createContext({
  setMode: (p) => {},
  toggle: () => {},
});

export function DarkModeProvider({
  children,
  storageKey = "lightMode",
  classNameDark = "light",
  classNameLight = "dark",
}) {
  const [darkMode, setDarkMode] = useState(false);

  const setMode = useCallback(
    (val) => {
      setDarkMode(val);
      localStorage.setItem(storageKey, val ? "true" : "false");
      document.body.classList.add(val ? classNameDark : classNameLight);
      document.body.classList.remove(val ? classNameLight : classNameDark);
    },
    [classNameDark, classNameLight, storageKey]
  );

  const toggle = useCallback(() => {
    setMode(!darkMode);
  }, [darkMode, setMode]);

  useEffect(() => {
    const listener = ({ matches }) => setMode(matches);

    const isDarkMode = document.body.classList.contains(classNameDark);
    setDarkMode(isDarkMode);

    const mql = matchMedia(preferDarkQuery);
    if (mql.media === preferDarkQuery) {
      mql.addEventListener("change", listener);
      return () => mql.removeEventListener("change", listener);
    }
  }, [classNameDark, setMode]);

  return (
    <DarkModeContext.Provider value={darkMode}>
      <DarkModeActionContext.Provider value={{ setMode, toggle }}>
        {children}
      </DarkModeActionContext.Provider>
    </DarkModeContext.Provider>
  );
}

export const useDarkModeValue = () => {
  return useContext(DarkModeContext);
};

export const useDarkModeAction = () => {
  return useContext(DarkModeActionContext);
};

export const useDarkMode = () => {
  return [useContext(DarkModeContext), useContext(DarkModeActionContext)];
};
